/**
 * Hydrogen signal runtime: readable signals with `.set()`; effects track
 * synchronously and flush from a microtask queue; computeds are lazy
 * (push-stale / pull-verify — a dependent runs only once a computed on its
 * path has recomputed to a different value).
 */
import { createOwner, disposeOwner, getOwner, registerOwnedCleanup, removeOwnedCleanup, resetOwner, runWithOwner, } from './owner.js';
import { describeUpdateLoop } from './signal-diagnostics.js';
import { subscribe } from './store.js';
import { readStoreSnapshot, snapshotUnchanged } from './store-snapshot.js';
export { createOwner, createRoot, disposeOwner, getOwner, onCleanup, runWithOwner, } from './owner.js';
const SIGNAL_SYMBOL = Symbol.for('hydrogen.signal');
/**
 * Graph identity tags (#903). Forward: a public handle's tag resolves to its
 * internal node. Reverse: the `graphHandle` field declared in each node
 * literal — #1596: a post-creation symbol property forced an out-of-object
 * `PropertyArray` per node (+16 B, ~6000 nodes after the bench's 10k-mutation
 * scenario); a literal field lives in-object.
 *
 * Every tagging site is gated on `graphSink !== null`: only
 * `hydrogen/introspection` registers a sink, so a page that never loads it
 * pays nothing — not even the property stores. Untagged handles (created
 * before registration) are rejected by `signalSubtle` as foreign objects;
 * dev-surface contract, not a kernel one.
 */
const SIGNAL_GRAPH_NODE = Symbol('hydrogen.signal.graphNode');
let graphSink = null;
/** Registers the graph feed, or null to detach it. Kernel-internal wiring. */
export function registerSignalGraphSink(sink) {
    graphSink = sink;
}
/**
 * Resolves a public handle to its graph node, or null for anything that is
 * not a tagged kernel object. Handles arrive opaque from devtools, so the
 * read runs through the tag type at this one boundary.
 */
export function graphNodeOf(handle) {
    const tagged = handle;
    const node = tagged?.[SIGNAL_GRAPH_NODE];
    return typeof node === 'object' && node !== null ? node : null;
}
/**
 * The public handle a node fronts, or null for kernel nodes that front none.
 */
export function graphHandleOf(node) {
    return node.graphHandle;
}
/**
 * The public handle a readable node fronts — a computed's own handle, or the
 * read function behind a writable signal's node — or null for kernel nodes
 * that front no public handle (`wakeNode()` tokens).
 */
export function sourceGraphHandle(source) {
    const handle = source.computed !== null ? source.computed.graphHandle : source.graphHandle;
    return handle === null ? null : handle;
}
/** Adds `value`, promoting a single edge to a `Set` on the second distinct one. */
function addEdge(edges, value) {
    if (edges === null || edges === value)
        return value;
    if (edges instanceof Set) {
        edges.add(value);
        return edges;
    }
    return new Set([edges, value]);
}
/**
 * Push-stale / pull-verify states (#1204). A write marks its direct
 * dependents `DIRTY`; anything reached only through a computed is marked
 * `CHECK`, which means "some upstream input changed, verify whether a value
 * on your path did". A `CHECK` node settles its computed sources first and
 * only runs if one of them recomputed to a different value.
 */
const CLEAN = 0;
const CHECK = 1;
const DIRTY = 2;
/**
 * Flush rounds a single `flushSignalQueue()` may take before it is declared a
 * loop (#1206). A chain of effects that each write a signal the next reads
 * settles in one round per link; a self-writing effect never settles. Vue
 * stops at the same bound.
 */
const MAX_FLUSH_ROUNDS = 100;
let nextEffectId = 1;
/** A computed that has never run holds this, so its first value always counts as a change. */
const UNSET = Symbol('hydrogen.unset');
let activeObserver = null;
const observerStack = [];
const queuedEffects = new Set();
let flushScheduled = false;
let batchDepth = 0;
function scheduleFlush() {
    if (batchDepth > 0)
        return;
    if (flushScheduled)
        return;
    flushScheduled = true;
    queueMicrotask(flushSignalQueue);
}
/** Reads without registering the active observer. */
export function untrack(fn) {
    const previous = activeObserver;
    activeObserver = null;
    try {
        return fn();
    }
    finally {
        activeObserver = previous;
    }
}
/**
 * Signal-level batch: writes inside `fn` queue effects but do not flush
 * until `fn` returns, then one `flushSignalQueue()`. Nested batches collapse.
 */
export function batch(fn) {
    batchDepth += 1;
    try {
        return fn();
    }
    finally {
        batchDepth -= 1;
        if (batchDepth === 0)
            flushSignalQueue();
    }
}
/** Unsubscribes one observer; a set emptied by the removal collapses to `null`. */
function detachDependent(source, observer) {
    const dependents = source.dependents;
    if (dependents === observer)
        source.dependents = null;
    else if (dependents instanceof Set) {
        dependents.delete(observer);
        if (dependents.size === 0)
            source.dependents = null;
    }
    if (source.release !== null && source.dependents === null)
        source.release();
}
function cleanupObserver(observer) {
    const sources = observer.dependencies;
    if (sources === null)
        return;
    observer.dependencies = null;
    if (sources instanceof Set) {
        for (const source of sources)
            detachDependent(source, observer);
        return;
    }
    detachDependent(sources, observer);
}
function track(source) {
    if (!isTracking())
        return;
    const observer = activeObserver;
    source.dependents = addEdge(source.dependents, observer);
    observer.dependencies = addEdge(observer.dependencies, source);
}
/**
 * Whether a read happening now would register a dependency.
 *
 * Kernel-internal, and the exact predicate `track` uses, so a caller cannot
 * disagree with it. `selector()` needs it because an index entry is only ever
 * paid for by a subscription: a key indexed on an untracked read has no
 * dependent, so nothing can ever leave and fire the release that drops it —
 * the entry, and the key object with it, would be retained for the life of the
 * selector. Application code has no use for this; the barrels do not export it.
 */
export function isTracking() {
    return activeObserver !== null && !activeObserver.disposed;
}
/**
 * Marks every dependent of `source` at least `state` stale. Direct
 * dependents of a changed value become `DIRTY`; the rest of the graph below a
 * computed becomes `CHECK` and is verified lazily. Effects queue on either
 * mark and decide whether to run when the flush reaches them.
 *
 * A single dependent — the common case — is marked with no iteration at all.
 * The many-dependent set is iterated in place. It used to be copied into an
 * array first, which cost one array the width of the set on EVERY signal write
 * — a 1,000-element allocation per write for a signal a 1,000-row list reads.
 * The copy is not needed, because of what can and cannot mutate the set here:
 *
 * - Nothing reached from this walk runs user code (effects are queued, not
 *   run, and `scheduleFlush` only posts a microtask), so `track()` cannot ADD
 *   a dependent mid-iteration, and no set can be promoted out from under the
 *   loop. `signal-dependent-marking.test.ts` holds that invariant: it drives
 *   every reachable branch through a source whose set is mutated by the walk
 *   and fails if a mark is lost.
 * - The one deletion is `cleanupObserver(observer)`, which unsubscribes
 *   `observer` — and only `observer` — from the sources it reads. Deleting
 *   the element currently being visited is defined `Set` iteration behaviour.
 * - The `CHECK` recursion below can reach a discarded computed that also
 *   depends on THIS source and sever it, deleting an element this loop has
 *   not visited yet, which `Set` iteration then skips. That element was left
 *   `DIRTY` by the severing, and `DIRTY` is the maximum `state` this function
 *   is ever called with, so the `observer.state >= state` guard would have
 *   skipped it anyway. Skipping it is the same outcome, not a lost mark.
 * - A severing that empties the set nulls `source.dependents`, but this loop
 *   holds the set object itself, so it finishes its walk of what is left
 *   rather than stopping early. That is the same set the old copy would have
 *   held, minus the entries the severing removed.
 */
function markDependents(source, state) {
    const dependents = source.dependents;
    if (dependents === null)
        return;
    if (dependents instanceof Set) {
        for (const observer of dependents)
            markDependent(observer, state);
        return;
    }
    markDependent(dependents, state);
}
function markDependent(observer, state) {
    if (observer.disposed)
        return;
    if (observer.state >= state) {
        // A DIRTY computed that threw still has to wake readers when a source
        // recovers (#1475). Skipping here leaves those readers CLEAN forever.
        if (observer.kind === 'computed' && observer.errored && observer.source.dependents !== null) {
            markDependents(observer.source, DIRTY);
        }
        return;
    }
    const previous = observer.state;
    observer.state = state;
    if (observer.kind === 'computed') {
        // A stale computed with no dependents cannot propagate to anyone, so
        // its source edges are dead weight: sever them. Laziness is preserved —
        // a later read sees `DIRTY`, recomputes, and re-tracks its sources.
        // Without this, every discarded computed (a per-row `computed()` in a
        // re-rendered list is the canonical case) stays subscribed to its
        // sources forever, growing dependent sets and retained heap per render.
        if (observer.source.dependents === null) {
            cleanupObserver(observer);
            observer.state = DIRTY;
            return;
        }
        if (previous === CLEAN)
            markDependents(observer.source, CHECK);
        return;
    }
    queuedEffects.add(observer);
    scheduleFlush();
}
function runTracked(observer) {
    cleanupObserver(observer);
    observerStack.push(activeObserver);
    activeObserver = observer;
    try {
        return observer.fn();
    }
    finally {
        activeObserver = observerStack.pop() ?? null;
    }
}
/**
 * Recomputes a computed and propagates only when the value changed (#1204).
 * Dependents that were merely `CHECK` become `DIRTY`; a dependent that had no
 * reason to wake stays asleep.
 */
function recompute(observer) {
    if (observer.computing) {
        throw new RangeError('computed() reads itself');
    }
    observer.computing = true;
    let next;
    try {
        next = runTracked(observer);
    }
    catch (error) {
        observer.errored = true;
        throw error;
    }
    finally {
        observer.computing = false;
    }
    observer.errored = false;
    const changed = observer.value === UNSET || !observer.equals(observer.value, next);
    observer.value = next;
    observer.state = CLEAN;
    if (changed)
        markDependents(observer.source, DIRTY);
}
/**
 * Brings an observer to a settled state: a `CHECK` node asks each computed
 * source to settle and becomes `DIRTY` only if one of them changed value; a
 * `DIRTY` computed recomputes. A `DIRTY` effect is left for the flush to run.
 */
function updateIfNecessary(observer) {
    if (observer.state === CHECK) {
        const sources = observer.dependencies;
        if (sources instanceof Set) {
            for (const source of sources) {
                if (source.computed)
                    updateIfNecessary(source.computed);
                if (observer.state === DIRTY)
                    break;
            }
        }
        else if (sources?.computed) {
            updateIfNecessary(sources.computed);
        }
        if (observer.state === CHECK)
            observer.state = CLEAN;
    }
    if (observer.state === DIRTY && observer.kind === 'computed') {
        recompute(observer);
    }
}
function disposeRunOwner(observer) {
    const runOwner = observer.runOwner;
    observer.runOwner = null;
    if (runOwner)
        disposeOwner(runOwner);
}
function runEffect(observer) {
    if (observer.disposed)
        return;
    // Child computations from the previous run go first (#1203), then the
    // run's own cleanup, so teardown happens inner-to-outer. The scope is reset,
    // not replaced (#1310): see `runOwner`.
    const runOwner = observer.runOwner;
    if (runOwner)
        resetOwner(runOwner);
    else
        observer.runOwner = createOwner(observer.parentOwner);
    if (typeof observer.cleanup === 'function') {
        observer.cleanup();
        observer.cleanup = null;
    }
    observer.state = CLEAN;
    const cleanup = runWithOwner(observer.runOwner, () => runTracked(observer));
    if (typeof cleanup === 'function') {
        observer.cleanup = cleanup;
    }
}
function settleEffect(observer, errors) {
    if (observer.disposed)
        return;
    try {
        try {
            updateIfNecessary(observer);
        }
        catch {
            // A computed source threw while being verified. That throw belongs to
            // the effect body, where an error boundary can contain it: run the body
            // and let it re-read the source instead of surfacing the error here.
            observer.state = DIRTY;
        }
        // Settling may have re-queued this effect (a source it checks changed);
        // it is about to run with that state, so a second run next round would
        // be a duplicate. Writes made by the run itself re-queue it afterwards.
        queuedEffects.delete(observer);
        if (observer.state !== DIRTY) {
            observer.state = CLEAN;
            return;
        }
        runEffect(observer);
    }
    catch (error) {
        errors.push(error);
    }
}
/**
 * Flushes all queued effects. Exported for tests; user code normally observes
 * the queue by awaiting a microtask.
 *
 * Every queued effect runs even when an earlier one throws (#1205): the loop
 * completes, then the first error is rethrown (an `AggregateError` when more
 * than one effect threw). A flush that cannot settle within
 * `MAX_FLUSH_ROUNDS` throws a `RangeError` naming the busiest effects
 * (#1206) instead of spinning the task forever.
 */
export function flushSignalQueue() {
    flushScheduled = false;
    const errors = [];
    let rounds = 0;
    while (queuedEffects.size > 0) {
        rounds += 1;
        if (rounds > MAX_FLUSH_ROUNDS) {
            const queued = [...queuedEffects];
            queuedEffects.clear();
            throw new RangeError(describeUpdateLoop(queued, MAX_FLUSH_ROUNDS));
        }
        const effects = [...queuedEffects];
        queuedEffects.clear();
        // Two passes over the same batch: everything that only computes, then
        // everything that writes to the DOM. Within one flush this means a
        // committing effect can no longer land a write between two reads and force
        // a reflow (#685). Order inside each pass is unchanged, and the loop still
        // runs to quiescence, so effects queued by this batch flush next round.
        for (const observer of effects) {
            if (!observer.commitsDom)
                settleEffect(observer, errors);
        }
        for (const observer of effects) {
            if (observer.commitsDom)
                settleEffect(observer, errors);
        }
    }
    if (errors.length > 0) {
        throw errors.length === 1
            ? errors[0]
            : new AggregateError(errors, 'effects threw during the flush');
    }
}
/**
 * A boolean wake token whose `onIdle` fires when its last tracking observer
 * leaves.
 *
 * Kernel-internal. `selector()` keeps one per key it has been asked about, and
 * `onIdle` is what bounds that index by the live subscriptions rather than by
 * every key ever read — an index that gains a key per row and never loses one
 * is the leak the primitive exists to avoid. Application code wanting teardown
 * notification wants `onCleanup` on the owner that owns the reader, so this is
 * deliberately absent from both barrels.
 *
 * The value is only a token: readers subscribe to be woken, and take their
 * answer from the settled source rather than from this. So it needs no
 * `peek` (nothing reads it untracked), no `update` (the selector is its only
 * writer and writes a literal), and no `subscribe` (nothing bridges it to a
 * store).
 */
export function wakeNode(initial, onIdle) {
    let value = initial;
    const source = {
        dependents: null,
        computed: null,
        release: onIdle,
        graphHandle: null,
    };
    const read = () => {
        track(source);
        return value;
    };
    read.set = (next) => {
        if (value === next)
            return;
        value = next;
        markDependents(source, DIRTY);
    };
    return read;
}
/**
 * Creates a writable signal.
 */
export function signal(initial) {
    return createWritable(initial);
}
function createWritable(initial) {
    let value = initial;
    const source = { dependents: null, computed: null, release: null, graphHandle: null };
    // `.subscribe()` is the imperative escape hatch — the store bridge and a
    // handful of adapters use it, template bindings never do. Allocating the
    // listener set up front therefore costs one empty Set per signal, and a
    // 1,000-row list creates a signal per row. Created on first subscribe.
    let listeners = null;
    function read() {
        track(source);
        return value;
    }
    read[SIGNAL_SYMBOL] = true;
    read.peek = () => value;
    read.set = (nextValue) => {
        const resolved = typeof nextValue === 'function' ? nextValue(value) : nextValue;
        if (Object.is(value, resolved))
            return value;
        value = resolved;
        markDependents(source, DIRTY);
        // The copy stays: a listener may subscribe or unsubscribe from inside the
        // notification, and this loop runs user code.
        if (listeners) {
            for (const listener of [...listeners]) {
                listener(value);
            }
        }
        return value;
    };
    read.update = (fn) => read.set(fn(value));
    read.subscribe = (listener) => {
        const set = (listeners ??= new Set());
        set.add(listener);
        return () => {
            set.delete(listener);
        };
    };
    if (graphSink !== null) {
        read[SIGNAL_GRAPH_NODE] = source;
        source.graphHandle = read;
        graphSink.track(read, 'signal', undefined);
    }
    return read;
}
/**
 * Creates a lazily evaluated derived signal.
 *
 * A recompute that yields an equal value (`Object.is`, or `options.equals`)
 * does not wake dependents (#1204): an effect bound to `isEmpty()` runs when
 * the boolean flips, not on every edit of the list behind it.
 */
export function computed(fn, options = {}) {
    const observer = {
        kind: 'computed',
        fn,
        dependencies: null,
        source: { dependents: null, computed: null, release: null, graphHandle: null },
        state: DIRTY,
        disposed: false,
        value: UNSET,
        equals: (options.equals ?? Object.is),
        computing: false,
        errored: false,
        graphHandle: null,
    };
    observer.source.computed = observer;
    function read() {
        // Settle before tracking: a reader that first subscribes here must not be
        // woken by the very recompute that produced the value it is reading.
        // Track even when settle throws so a recovered source can re-mark this reader (#1475).
        try {
            updateIfNecessary(observer);
        }
        finally {
            track(observer.source);
        }
        return observer.value;
    }
    read[SIGNAL_SYMBOL] = true;
    read.peek = () => {
        updateIfNecessary(observer);
        return observer.value;
    };
    let registrationOwner = null;
    const dispose = () => {
        if (observer.disposed)
            return;
        observer.disposed = true;
        // Explicit disposal releases its own registration (#1395, #1479), so a
        // long-lived owner does not retain one disposer closure per
        // create/dispose cycle.
        if (registrationOwner !== null)
            removeOwnedCleanup(registrationOwner, dispose);
        graphSink?.forget(read);
        cleanupObserver(observer);
        observer.source.dependents = null;
    };
    registrationOwner = registerOwnedCleanup(dispose);
    read.dispose = dispose;
    if (graphSink !== null) {
        read[SIGNAL_GRAPH_NODE] = observer;
        observer.graphHandle = read;
        graphSink.track(read, 'computed', options.name);
    }
    return read;
}
/**
 * Runs a reactive side effect immediately, then again after tracked inputs
 * change. The returned function disposes the effect and any cleanup it returned.
 *
 * Each run is an owner scope (#1203): effects, computeds and store bridges
 * created inside the body belong to that run and are disposed before the next
 * one, and when the effect itself is disposed.
 */
export function effect(fn, options = {}) {
    const observer = {
        kind: 'effect',
        fn,
        dependencies: null,
        disposed: false,
        cleanup: null,
        state: DIRTY,
        id: nextEffectId,
        name: options.name,
        parentOwner: getOwner(),
        runOwner: null,
        commitsDom: options.commitsDom === true,
        graphHandle: null,
    };
    nextEffectId += 1;
    // The disposer exists and is registered BEFORE the first run (#1402): a
    // body that throws on setup must leave behind an effect its owner can
    // still dispose — and therefore unsubscribe — rather than an orphan that
    // keeps re-running on dependency changes with a run scope nobody owns.
    // Disposal releases its own registration first (#1395), so a long-lived
    // owner does not retain one disposer closure per create/dispose cycle.
    let registrationOwner = null;
    const dispose = () => {
        if (observer.disposed)
            return;
        observer.disposed = true;
        graphSink?.forget(dispose);
        if (registrationOwner !== null)
            removeOwnedCleanup(registrationOwner, dispose);
        queuedEffects.delete(observer);
        cleanupObserver(observer);
        disposeRunOwner(observer);
        if (typeof observer.cleanup === 'function') {
            observer.cleanup();
            observer.cleanup = null;
        }
    };
    registrationOwner = registerOwnedCleanup(dispose);
    if (graphSink !== null) {
        dispose[SIGNAL_GRAPH_NODE] = observer;
        observer.graphHandle = dispose;
        graphSink.track(dispose, 'effect', options.name);
    }
    runEffect(observer);
    return dispose;
}
/**
 * Bridges the global Hydrogen store into a read-only signal.
 *
 * A store batch triggers one store notification, which in turn schedules one
 * signal flush for any dependent effects or template parts. A notification
 * whose watched values are identity-unchanged is dropped here — the fresh
 * snapshot object alone must not re-run every dependent (#681).
 */
export function storeSignal(keys) {
    let keyList;
    if (keys === undefined || keys === null) {
        keyList = null;
    }
    else if (Array.isArray(keys)) {
        keyList = keys;
    }
    else {
        keyList = [keys];
    }
    const returnObject = Array.isArray(keys) || keyList === null;
    const source = signal(readStoreSnapshot(keyList, returnObject));
    const publish = () => {
        const next = readStoreSnapshot(keyList, returnObject);
        if (returnObject && snapshotUnchanged(source.peek(), next))
            return;
        source.set(next);
    };
    const unsubscribe = keyList ? subscribe(keyList, publish) : subscribe(publish);
    function read() {
        return source();
    }
    read[SIGNAL_SYMBOL] = true;
    read.peek = source.peek;
    let registrationOwner = null;
    const dispose = () => {
        // Same self-releasing registration as computed()/effect() (#1395, #1479):
        // an explicit dispose must drop its owner entry, not leak a closure per
        // bridge cycle.
        if (registrationOwner !== null)
            removeOwnedCleanup(registrationOwner, dispose);
        unsubscribe();
    };
    read.dispose = dispose;
    registrationOwner = registerOwnedCleanup(dispose);
    return read;
}
/**
 * Runtime-internal predicate used by the renderer to distinguish readable
 * signals from ordinary callback functions such as event handlers.
 */
export function isSignal(value) {
    return (typeof value === 'function' && !!value[SIGNAL_SYMBOL]);
}
// ---------------------------------------------------------------------------
// Introspection wiring (#903)
// ---------------------------------------------------------------------------
// The `Signal.subtle`-shaped read path, the graph feed and the devtools
// contract live in `signal-introspection.ts` — a kernel sibling like
// `signal-selector.ts` — so this file stays inside its per-module size
// budget. The graph lives here; the readers there.
/**
 * The observer currently evaluating, or null. Exposed for
 * `signal-introspection.ts`'s `currentComputed`, which must see the kernel's
 * tracking state without this module importing the feed back.
 */
export function activeGraphObserver() {
    return activeObserver;
}
//# sourceMappingURL=signal.js.map